/**
 * Pure presentation for the AI Case Summary card. The connected card in
 * `features/case-overview` resolves the cheap status pre-flight + cached
 * payload into one of the explicit states below and supplies the action
 * callbacks (generate / regenerate / download).
 *
 * Four render states, selected by props:
 *
 *   - ``loading``  → skeleton row.
 *   - ``empty``    → no eligible investigations yet (CTA → investigations).
 *   - ``generate`` → eligible investigations marked but no summary built.
 *   - ``summary``  → cached summary (executive narrative + the hypotheses with
 *                    their latest assessment + per-investigation findings +
 *                    regenerate/download actions).
 */

import {
  ArrowRight,
  Download,
  FileCheck2,
  FileText,
  Loader2,
  RefreshCw,
  Sparkles,
  StickyNote,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  HypothesesDigestView,
  type HypothesisDigestItem,
} from '@/components/analytic-hypotheses/HypothesesDigestView'
import { EvidenceSourceChip } from '@/components/evidence/EvidenceSourceChip'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import type {
  CaseSummaryEnvelope,
  CaseSummaryHypothesis,
  CaseSummaryInvestigation,
} from '@/services/api/cases'
import { formatRelative } from '@/utils/date'

export interface CaseSummaryCardViewProps {
  /** Resolved render state for the card. */
  state: 'loading' | 'empty' | 'generate' | 'summary'
  /** Number of investigations marked for the report (state `generate`). */
  eligibleCount: number
  /** Cached summary payload (state `summary`). */
  envelope: CaseSummaryEnvelope | null
  /** Destination for the "Start research" / "Manage investigations" CTAs. */
  investigationsLink: string
  /** When false, suppress the per-investigation mini-rows and the hypotheses in the summary
   *  state (the Case summary page shows both on its own). */
  showInvestigationList?: boolean
  /** Whether a generate/regenerate mutation is in flight. */
  isGenerating: boolean
  /** Whether a PDF download is in flight. */
  isDownloading: boolean
  onGenerate: () => void
  onDownload: () => void
}

export function CaseSummaryCardView({
  state,
  eligibleCount,
  envelope,
  investigationsLink,
  showInvestigationList = true,
  isGenerating,
  isDownloading,
  onGenerate,
  onDownload,
}: CaseSummaryCardViewProps) {
  const { i18n, t } = useTranslation()
  const justNow = t('overview.justNow')

  // ── Loading skeleton ───────────────────────────────────────────────
  if (state === 'loading' || (state === 'summary' && !envelope)) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          <span className="text-sm">{t('cases.summaryCard.loading', 'Loading case summary…')}</span>
        </div>
      </Card>
    )
  }

  // ── State 1: no eligible investigations ────────────────────────────
  if (state === 'empty') {
    return (
      <Card className="p-5 md:p-8">
        <div className="flex flex-col items-center text-center">
          <div className="flex size-14 items-center justify-center rounded-2xl border border-primary/40 bg-primary/10 text-primary">
            <Sparkles className="size-7" />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-foreground">
            {t('cases.summaryCard.title', 'AI Case Summary')}
          </h3>
          <p className="mt-2 max-w-md text-sm text-muted-foreground">
            {t(
              'cases.summaryCard.emptyIntro',
              'A short narrative summary of this case can be generated once you have at least one investigation marked as Add to report. Start by exploring documents and saving investigations.'
            )}
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <Button asChild size="lg" className="gap-2">
              <Link to={investigationsLink}>
                {t('cases.summaryCard.startResearch', 'Start research')}
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </Card>
    )
  }

  // ── State 2: eligible investigations marked but no summary yet ────
  if (state === 'generate') {
    return (
      <Card className="p-5 md:p-8">
        <div className="flex flex-col items-center text-center">
          <div className="flex size-14 items-center justify-center rounded-2xl border border-primary/40 bg-primary/10 text-primary">
            <Sparkles className="size-7" />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-foreground">
            {t('cases.summaryCard.title', 'AI Case Summary')}
          </h3>
          <p className="mt-2 max-w-md text-sm text-muted-foreground">
            {t('cases.summaryCard.eligibleCount', {
              defaultValue: '{{count}} investigation is marked for the report.',
              count: eligibleCount,
            })}{' '}
            {t(
              'cases.summaryCard.generateIntro',
              'Generate an executive narrative + per-investigation findings now — you can refresh it later as the case evolves.'
            )}
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <Button size="lg" className="gap-2" onClick={onGenerate} disabled={isGenerating}>
              {isGenerating ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {t('cases.summaryCard.generating', 'Generating…')}
                </>
              ) : (
                <>
                  <Sparkles className="size-4" />
                  {t('cases.summaryCard.generate', 'Generate Case Summary')}
                </>
              )}
            </Button>
            <Button asChild size="lg" variant="outline" className="gap-2">
              <Link to={investigationsLink}>
                {t('cases.summaryCard.manageInvestigations', 'Manage investigations')}
              </Link>
            </Button>
          </div>
        </div>
      </Card>
    )
  }

  // ── State 3: render cached summary ─────────────────────────────────
  // `envelope` is guaranteed non-null here (the loading guard above covers
  // the `summary && !envelope` case).
  const safeEnvelope = envelope as CaseSummaryEnvelope
  const payload = safeEnvelope.payload
  const investigations = payload.investigations ?? []
  const generatedAt = safeEnvelope.generated_at

  return (
    <Card className="p-4 md:p-6">
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-primary/40 bg-primary/10 text-primary">
          <Sparkles className="size-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-foreground">
            {t('cases.summaryCard.title', 'AI Case Summary')}
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('cases.summaryCard.generatedMeta', {
              defaultValue:
                'Generated {{when}} · covering {{count}} investigations marked for the report',
              when: formatRelative(generatedAt, i18n.language, justNow),
              count: investigations.length,
            })}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2 max-md:w-full max-md:flex-wrap">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 max-md:h-9 max-md:flex-1"
            onClick={onGenerate}
            disabled={isGenerating}
            title={t(
              'cases.summaryCard.regenerateTitle',
              'Regenerate the summary using the latest investigation data'
            )}
          >
            {isGenerating ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <RefreshCw className="size-3.5" />
            )}
            {t('cases.summaryCard.regenerate', 'Regenerate')}
          </Button>
          <Button
            size="sm"
            className="gap-1.5 max-md:h-9 max-md:flex-1"
            onClick={onDownload}
            disabled={isDownloading}
          >
            {isDownloading ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Download className="size-3.5" />
            )}
            {t('cases.summaryCard.downloadPdf', 'Download PDF report')}
          </Button>
        </div>
      </div>

      {/* Executive summary — labeled section that mirrors the PDF's
          "01 · SUMMARY / Executive summary" block. Skipped silently if the
          LLM response was empty (e.g. a backend stub run). */}
      {payload.ai_summary && (
        <section className="mt-6 border-t border-border/40 pt-5 md:rounded-xl md:border md:bg-muted/30 md:p-5">
          <div className="flex items-center gap-2 text-3xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            <span className="h-3 w-px bg-primary" />
            {t('cases.summaryCard.sectionSummary', '01 · Summary')}
          </div>
          <h4 className="mt-2 text-xl font-semibold tracking-tight text-foreground">
            {t('cases.summaryCard.executiveSummary', 'Executive summary')}
          </h4>
          <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-foreground/90">
            {payload.ai_summary}
          </p>

          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile
              label={t('cases.summaryCard.stats.investigations', 'Investigations')}
              value={investigations.length}
            />
            <StatTile
              label={t('cases.summaryCard.stats.entities', 'Entities')}
              value={payload.case.entityCount}
            />
            <StatTile
              label={t('cases.summaryCard.stats.relationships', 'Relationships')}
              value={payload.case.relationshipCount}
            />
            <StatTile
              label={t('cases.summaryCard.stats.documents', 'Documents')}
              value={payload.case.documentCount}
            />
          </div>
        </section>
      )}

      {showInvestigationList && payload.hypotheses && payload.hypotheses.length > 0 && (
        <div className="mt-6">
          <HypothesesDigestView items={payload.hypotheses.map(digestItem)} />
        </div>
      )}

      {showInvestigationList && investigations.length > 0 && (
        <section className="mt-6 space-y-4">
          <div className="flex items-center gap-2 text-3xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            <span className="h-3 w-px bg-primary" />
            {t('cases.summaryCard.sectionInvestigations', '02 · Investigations')}
          </div>
          <h4 className="text-base font-semibold text-foreground">
            {t('cases.summaryCard.savedInReport', 'Saved investigations in report')}
          </h4>
          {investigations.map((inv, idx) => (
            <InvestigationSummaryRow key={`${inv.title}-${idx}`} inv={inv} />
          ))}
        </section>
      )}
    </Card>
  )
}

/** A hypothesis of the cached payload as the digest lists it (the payload carries no id). */
function digestItem(hypothesis: CaseSummaryHypothesis, index: number): HypothesisDigestItem {
  return {
    key: `${index}-${hypothesis.title}`,
    title: hypothesis.title,
    verdict: hypothesis.verdict,
    confidence: hypothesis.confidence,
    assessedAt: hypothesis.verdict ? hypothesis.assessedAt : null,
  }
}

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border/40 bg-card p-3">
      <div className="font-mono text-2xl font-semibold tabular-nums text-foreground">{value}</div>
      <div className="mt-1 text-3xs font-medium uppercase tracking-widest text-muted-foreground/70">
        {label}
      </div>
    </div>
  )
}

function InvestigationSummaryRow({ inv }: { inv: CaseSummaryInvestigation }) {
  const { t } = useTranslation()
  const verdictClass =
    inv.confidence === 'supported'
      ? 'border-success/40 bg-success/10 text-success'
      : inv.confidence === 'refuted'
        ? 'border-destructive/40 bg-destructive/10 text-destructive'
        : 'border-warning/40 bg-warning/10 text-warning'

  return (
    <div className="rounded-xl border border-border/40 bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h5 className="text-sm font-semibold text-foreground">{inv.title}</h5>
          <p className="mt-0.5 text-2xs text-muted-foreground">
            {inv.author && <span>{inv.author}</span>}
            {inv.author && ' · '}
            <span className="font-mono">{inv.updatedAt}</span>
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
          {inv.isReport && (
            <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-3xs font-semibold uppercase tracking-wider text-primary">
              <FileCheck2 className="size-3" />
              {t('cases.summaryCard.researchReport')}
            </span>
          )}
          {inv.confidence && (
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-3xs font-semibold uppercase tracking-wider ${verdictClass}`}
            >
              {inv.confidence}
            </span>
          )}
          <span className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-muted/40 px-2 py-0.5 text-3xs text-muted-foreground">
            <FileText className="size-3" />
            <span className="font-mono tabular-nums">{inv.entitiesTotal}</span>
            {t('cases.summaryCard.entities', 'entities')}
          </span>
          {inv.noteCount > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-muted/40 px-2 py-0.5 text-3xs text-muted-foreground">
              <StickyNote className="size-3" />
              <span className="font-mono tabular-nums">{inv.noteCount}</span>
            </span>
          )}
        </div>
      </div>

      {inv.summary && (
        <p className="mt-3 whitespace-pre-line text-xs leading-relaxed text-foreground">
          {inv.summary}
        </p>
      )}

      {inv.relations.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {inv.relations.map(rel => (
            <span
              key={rel.type}
              className="inline-flex items-center gap-1 rounded-md border border-border/40 bg-muted/30 px-2 py-0.5 font-mono text-3xs text-muted-foreground"
            >
              {rel.type}
              <span className="text-foreground/60">{rel.count}</span>
            </span>
          ))}
        </div>
      )}

      {inv.evidence.length > 0 && (
        <div className="mt-3 space-y-1">
          <p className="text-3xs font-medium uppercase tracking-widest text-muted-foreground/60">
            {t('cases.summaryCard.evidence', {
              defaultValue: 'Evidence ({{count}})',
              count: inv.evidenceCount,
            })}
          </p>
          <ul className="space-y-1.5 text-xs text-foreground/90">
            {inv.evidence.slice(0, 5).map((ev, i) => (
              <li key={i} className="flex flex-col gap-1 leading-snug">
                <span>{ev.text}</span>
                {ev.label ? (
                  <span className="text-3xs text-muted-foreground">{ev.label}</span>
                ) : (
                  ev.source && <EvidenceSourceChip source={ev.source} />
                )}
              </li>
            ))}
            {inv.evidence.length > 5 && (
              <li className="font-mono text-3xs text-muted-foreground">
                {t('cases.summaryCard.evidenceMore', {
                  defaultValue: '+{{count}} more',
                  count: inv.evidence.length - 5,
                })}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
